/**
 * Phase 5 — Global Logistics Tracking Page
 * Shows all order_shipments across all orders with tracking links.
 */
import { useEffect, useState } from "react";
import { useRedirect } from "ra-core";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Package,
  RefreshCw,
  Search,
  Truck,
} from "lucide-react";

const PAGE_SIZE = 50;

// ─── Types ────────────────────────────────────────────────────────────────────
interface ShipmentRow {
  id: number;
  order_id: number;
  order_number: string | null;
  contact_name: string | null;
  company_name: string | null;
  shipment_number: string | null;
  carrier: string;
  tracking_number: string | null;
  items_description: string | null;
  total_cartons: number | null;
  actual_ship_date: string | null;
  estimated_arrival_date: string | null;
  actual_arrival_date: string | null;
  notes: string | null;
  created_at: string;
}

// ─── Carrier tracking URLs ────────────────────────────────────────────────────
const CARRIER_LABELS: Record<string, string> = {
  fedex: "FedEx",
  ups: "UPS",
  dhl: "DHL",
  usps: "USPS",
  "4px": "4PX",
  sea: "Sea Freight",
  air: "Air Freight",
  other: "Other",
};

const TRACK_URLS: Record<string, string> = {
  fedex: "https://www.fedex.com/fedextrack/?trknbr={t}",
  ups: "https://www.ups.com/track?tracknum={t}",
  dhl: "https://www.dhl.com/en/express/tracking.html?AWB={t}",
  usps: "https://tools.usps.com/go/TrackConfirmAction?tLabels={t}",
  "4px": "https://track.4px.com/#/result/0/{t}",
};

const getTrackUrl = (carrier: string, tracking: string) => {
  const tmpl = TRACK_URLS[carrier];
  return tmpl ? tmpl.replace("{t}", encodeURIComponent(tracking)) : null;
};

// ─── Status badge ─────────────────────────────────────────────────────────────
const getShipmentBadge = (row: ShipmentRow) => {
  if (row.actual_arrival_date)
    return { label: "Delivered", variant: "outline" as const };
  if (row.actual_ship_date)
    return { label: "In Transit", variant: "default" as const };
  return { label: "Pending", variant: "secondary" as const };
};

// ─── Status tabs ──────────────────────────────────────────────────────────────
const TABS = [
  { key: "all", label: "All" },
  { key: "in_transit", label: "In Transit" },
  { key: "delivered", label: "Delivered" },
];

// ─── Main component ───────────────────────────────────────────────────────────
const LogisticsList = () => {
  const redirect = useRedirect();
  const [rows, setRows] = useState<ShipmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(0); // 0–100
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    getSupabaseClient()
      .from("order_shipments")
      .select(
        `
        id, order_id, shipment_number, carrier, tracking_number,
        items_description, total_cartons, actual_ship_date,
        estimated_arrival_date, actual_arrival_date, notes, created_at,
        orders!inner (
          order_number,
          contacts ( first_name, last_name ),
          companies ( name )
        )
      `,
      )
      .order("created_at", { ascending: false })
      .limit(200)
      .then(({ data }) => {
        if (data) {
          setRows(
            data.map((r: Record<string, unknown>) => {
              const order = r.orders as Record<string, unknown> | null;
              const contact = order?.contacts as Record<string, unknown> | null;
              const company = order?.companies as Record<
                string,
                unknown
              > | null;
              return {
                id: r.id as number,
                order_id: r.order_id as number,
                order_number: (order?.order_number as string) ?? null,
                contact_name: contact
                  ? `${contact.first_name} ${contact.last_name}`
                  : null,
                company_name: (company?.name as string) ?? null,
                shipment_number: r.shipment_number as string | null,
                carrier: r.carrier as string,
                tracking_number: r.tracking_number as string | null,
                items_description: r.items_description as string | null,
                total_cartons: r.total_cartons as number | null,
                actual_ship_date: r.actual_ship_date as string | null,
                estimated_arrival_date: r.estimated_arrival_date as
                  | string
                  | null,
                actual_arrival_date: r.actual_arrival_date as string | null,
                notes: r.notes as string | null,
                created_at: r.created_at as string,
              };
            }),
          );
        }
        setLoading(false);
      });
  }, []);

  // Reset to page 1 when tab or search changes
  useEffect(() => { setPage(1); }, [tab, search]);

  const filtered = rows.filter((r) => {
    // Tab filter
    if (tab === "in_transit" && (!r.actual_ship_date || r.actual_arrival_date))
      return false;
    if (tab === "delivered" && !r.actual_arrival_date) return false;

    // Search filter
    if (search) {
      const q = search.toLowerCase();
      return (
        r.tracking_number?.toLowerCase().includes(q) ||
        r.order_number?.toLowerCase().includes(q) ||
        r.shipment_number?.toLowerCase().includes(q) ||
        r.company_name?.toLowerCase().includes(q) ||
        r.contact_name?.toLowerCase().includes(q) ||
        CARRIER_LABELS[r.carrier]?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Manual tracking refresh — updates last_tracked_at for all in-transit shipments
  const handleRefreshTracking = async () => {
    const inTransit = rows.filter(
      (r) => r.actual_ship_date && !r.actual_arrival_date && r.tracking_number,
    );
    if (inTransit.length === 0) return;
    setRefreshing(true);
    setRefreshProgress(0);
    const supabase = getSupabaseClient();
    const now = new Date().toISOString();
    for (let i = 0; i < inTransit.length; i++) {
      await supabase
        .from("order_shipments")
        .update({ last_tracked_at: now })
        .eq("id", inTransit[i].id);
      setRefreshProgress(Math.round(((i + 1) / inTransit.length) * 100));
    }
    setRefreshing(false);
    setRefreshProgress(100);
    setTimeout(() => setRefreshProgress(0), 3000);
  };

  const copyTracking = (row: ShipmentRow) => {
    if (!row.tracking_number) return;
    navigator.clipboard.writeText(row.tracking_number).then(() => {
      setCopied(row.id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-muted-foreground" />
          <h1 className="text-xl font-semibold">Logistics</h1>
          {!loading && (
            <span className="text-xs text-muted-foreground ml-1">
              {filtered.length} shipment{filtered.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Order #, tracking, company..."
              className="h-8 pl-8 text-xs"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleRefreshTracking}
            disabled={refreshing}
            className="h-8 gap-1.5 text-xs"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? `更新中 ${refreshProgress}%` : "刷新跟踪"}
          </Button>
        </div>
      </div>

      {/* Refresh progress bar */}
      {(refreshing || refreshProgress === 100) && (
        <div className="w-full bg-muted rounded-full h-1 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${refreshProgress === 100 ? "bg-green-500" : "bg-primary"}`}
            style={{ width: `${refreshProgress}%` }}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
            {t.key !== "all" && (
              <span className="ml-1.5 text-xs text-muted-foreground">
                {t.key === "in_transit"
                  ? rows.filter(
                      (r) => r.actual_ship_date && !r.actual_arrival_date,
                    ).length
                  : rows.filter((r) => !!r.actual_arrival_date).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Table header */}
      <div className="hidden md:grid grid-cols-[180px_1fr_160px_120px_110px_110px] gap-3 px-3 py-1.5 text-xs text-muted-foreground font-medium border-b">
        <div>Order / Batch</div>
        <div>Customer / Description</div>
        <div>Tracking</div>
        <div>Carrier</div>
        <div>Ship Date</div>
        <div>ETA / Arrived</div>
      </div>

      {/* Rows */}
      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No shipments found
        </div>
      ) : (
        <div className="space-y-1.5">
          {paged.map((row) => {
            const badge = getShipmentBadge(row);
            const trackUrl = row.tracking_number
              ? getTrackUrl(row.carrier, row.tracking_number)
              : null;

            return (
              <div
                key={row.id}
                className="grid grid-cols-[1fr_auto] md:grid-cols-[180px_1fr_160px_120px_110px_110px] gap-3 items-center px-3 py-2.5 bg-card border rounded-lg hover:bg-accent/40 transition-colors"
              >
                {/* Order / Batch */}
                <div>
                  <div
                    className="font-medium text-sm text-primary cursor-pointer hover:underline"
                    onClick={() => redirect("show", "orders", row.order_id)}
                  >
                    {row.order_number ??
                      `ORD-${String(row.order_id).padStart(4, "0")}`}
                  </div>
                  {row.shipment_number && (
                    <div className="text-xs text-muted-foreground">
                      {row.shipment_number}
                    </div>
                  )}
                  <Badge
                    variant={badge.variant}
                    className="text-[10px] mt-0.5 h-4 px-1.5"
                  >
                    {badge.label}
                  </Badge>
                </div>

                {/* Customer / Description */}
                <div className="min-w-0">
                  <div className="text-sm truncate">
                    {row.company_name ?? row.contact_name ?? "—"}
                  </div>
                  {row.items_description && (
                    <div className="text-xs text-muted-foreground truncate">
                      {row.items_description}
                    </div>
                  )}
                  {row.total_cartons && (
                    <div className="text-xs text-muted-foreground">
                      {row.total_cartons} ctns
                    </div>
                  )}
                </div>

                {/* Tracking */}
                <div className="hidden md:flex items-center gap-1.5">
                  {row.tracking_number ? (
                    <>
                      <span className="font-mono text-xs truncate flex-1">
                        {row.tracking_number}
                      </span>
                      <button
                        onClick={() => copyTracking(row)}
                        className="text-muted-foreground hover:text-foreground shrink-0"
                        title="Copy"
                      >
                        {copied === row.id ? (
                          <Check className="w-3 h-3 text-green-500" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </button>
                      {trackUrl && (
                        <a
                          href={trackUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground shrink-0"
                          title="Track"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </div>

                {/* Carrier */}
                <div className="hidden md:block text-sm">
                  {CARRIER_LABELS[row.carrier] ?? row.carrier}
                </div>

                {/* Ship Date */}
                <div className="hidden md:block text-xs text-muted-foreground">
                  {row.actual_ship_date ?? "—"}
                </div>

                {/* ETA / Arrived */}
                <div className="hidden md:block text-xs">
                  {row.actual_arrival_date ? (
                    <span className="text-green-600 font-medium">
                      ✓ {row.actual_arrival_date}
                    </span>
                  ) : row.estimated_arrival_date ? (
                    <span className="text-muted-foreground">
                      {row.estimated_arrival_date}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination bar */}
      {!loading && filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between pt-2 border-t text-sm text-muted-foreground">
          <span>
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} / 共 {filtered.length} 条
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1 rounded hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                if (idx > 0 && (arr[idx - 1] as number) + 1 < p) acc.push("…");
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === "…" ? (
                  <span key={`ellipsis-${i}`} className="px-1">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p as number)}
                    className={`w-7 h-7 rounded text-xs font-medium ${
                      page === p ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1 rounded hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogisticsList;
